import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useUserRole } from "@/hooks/useUserRole";
import { useAuth } from "@/hooks/useAuth";
import { useState, useEffect } from "react";

// Extend Window interface
declare global {
  interface Window {
    __HS_APP_READY__?: boolean;
  }
}

export function PublicTopNav() {
  const navigate = useNavigate();
  const { role } = useUserRole();
  const { user } = useAuth();
  
  // Always start hidden to prevent flash, then show after app is ready
  const [shouldShow, setShouldShow] = useState(false);
  
  useEffect(() => {
    let checkInterval: NodeJS.Timeout | null = null;
    let timeout: NodeJS.Timeout | null = null;
    
    // Use requestAnimationFrame to ensure we check after React has fully rendered
    const frameId = requestAnimationFrame(() => {
      // Check if app is ready
      if (window.__HS_APP_READY__) {
        setShouldShow(true);
        return;
      }
      
      // Check root element as fallback
      const root = document.getElementById('root');
      if (root && !root.classList.contains('hs-hidden')) {
        setShouldShow(true);
        return;
      }
      
      // If still not ready, check periodically
      checkInterval = setInterval(() => {
        if (window.__HS_APP_READY__) {
          setShouldShow(true);
          if (checkInterval) clearInterval(checkInterval);
        }
      }, 10);
      
      // Fallback: show after 200ms max to prevent long delay
      timeout = setTimeout(() => {
        setShouldShow(true);
        if (checkInterval) clearInterval(checkInterval);
      }, 200);
    });
    
    return () => {
      cancelAnimationFrame(frameId);
      if (checkInterval) clearInterval(checkInterval);
      if (timeout) clearTimeout(timeout);
    };
  }, []);

  const handleLogoClick = () => {
    navigate("/");
  };

  const handleDashboardClick = () => {
    if (role === 'supplier') {
      navigate("/supplier/dashboard", { replace: true });
    } else if (role === 'admin') {
      navigate("/admin/dashboard", { replace: true });
    } else {
      navigate("/buyer/dashboard", { replace: true });
    }
  };

  // Don't render until app is ready to prevent flash
  if (!shouldShow) {
    return null;
  }

  return (
    <nav className="sticky top-0 z-40 border-b bg-background">
      <div className="mx-auto flex max-w-7xl items-center justify-between p-4 sm:px-6">
        <button
          onClick={handleLogoClick}
          className="text-xl font-bold text-primary transition-opacity hover:opacity-80 sm:text-2xl"
        >
          HoleScale.
        </button>
        <div className="flex gap-2 sm:gap-4">
          {user ? (
            <Button onClick={handleDashboardClick} size="sm" className="text-sm sm:text-base">
              Dashboard
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => navigate("/auth")} size="sm" className="text-sm sm:text-base">
                Login
              </Button>
              <Button onClick={() => navigate("/auth?mode=signup")} size="sm" className="text-sm font-semibold sm:text-base">
                Sign Up Free
              </Button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
